import { useForm, SubmitHandler, useWatch } from 'react-hook-form';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { UserApi, AgreementPayload, ErrorResponse } from '../../api';
import { useToast } from '../../components/atoms/Toast';

interface Props {
  onClose: () => void;
}

export const useAgreementsModal = ({ onClose }: Props) => {
  const { data: user } = useQuery('user', UserApi.fetch);
  const agreements = user?.agreements || [];
  const methods = useForm<AgreementPayload>({
    defaultValues: {
      agreements: user?.agreements,
    },
  });

  const { handleSubmit, control } = methods;
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation<
    number[],
    ErrorResponse,
    AgreementPayload
  >(data => UserApi.editAgreements(data), {
    onSuccess() {
      queryClient.invalidateQueries('user');
    },
  });
  const { toast } = useToast();
  const onSubmit: SubmitHandler<AgreementPayload> = async (values) => {
    const transformedValues = {
      ...values,
      agreements: values.agreements.map((agreement) => ({
        ...agreement,
        has_agree: agreement.has_agree ? 1 : 0,
      })),
    };
    try {
      await mutateAsync(transformedValues);
      window.dispatchEvent(new CustomEvent('agreements-saved'));

      toast({
        variant: 'success',
        description: 'Zaktualizowano klauzule',
      });
      onClose();

    } catch (error) {
      toast({
        variant: 'error',
        description: 'Coś poszło nie tak!',
      });
    }
  };

  const watchedAgreements = useWatch({
    control,
    name: 'agreements',
    defaultValue: agreements,
  });

  const shouldDisable = agreements.some(
    (agreement, index) =>
      agreement.required && !watchedAgreements?.[index]?.has_agree,
  );

  const requiredAgreements = agreements.filter(agreement => agreement.required);
  const optionalAgreements = agreements.filter(agreement => !agreement.required);

  return {
    methods,
    handleSubmit,
    requiredAgreements,
    optionalAgreements,
    shouldDisable,
    onSubmit,
  };
};
